import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "color", "currency", "format", "hasColor", "id", "isCompact", "isUnstyled", "locale", "maximumFractionDigits", "minimumFractionDigits", "value"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { forwardRef } from 'react';
import cx from 'classnames';
import { prefix } from '../../tools/settings';
import { StyledFormatCurrency } from './FormatCurrency.styles';
import getSymbolFromCurrency from 'currency-symbol-map';
import { defaultLocales, locales } from './FormatCurrency.constants';
import { jsx as _jsx } from "react/jsx-runtime";

function toFixed(n, precision) {
  return (Math.round(Number(n) * Math.pow(10, precision)) / Math.pow(10, precision)).toFixed(precision);
}

function getFormatDetails(p) {
  var locale, currency, symbol, pattern, decimal, group, formatted, valueOnError;
  p = p || {};
  currency = (p.currency || 'USD').toUpperCase();
  locale = locales[p.locale || defaultLocales[currency]];

  if (typeof locale.h !== 'undefined') {
    locale = locales[locale.h];
  }

  symbol = typeof p.symbol === 'undefined' ? getSymbolFromCurrency(currency) || currency : p.symbol === null ? '' : p.symbol;
  pattern = p.pattern || locale.p;
  decimal = p.decimal || locale.d;
  formatted = p.formatted || 0;
  group = p.group || locale.g;
  valueOnError = typeof p.valueOnError === 'undefined' ? 0 : p.valueOnError;
  var formatDetails = {
    pattern: pattern,
    decimal: decimal,
    group: group,
    symbol: symbol,
    formatted: formatted,
    valueOnError: valueOnError,
    postFormatFunction: p.postFormatFunction
  };
  return formatDetails;
}

function getFormatter(p) {
  var formatDetails = getFormatDetails(p);
  var pattern = formatDetails.pattern;
  var decimal = formatDetails.decimal;
  var group = formatDetails.group;
  var symbol = formatDetails.symbol;
  var formatted = formatDetails.formatted;
  var valueOnError = formatDetails.valueOnError;
  var postFormatFunction = formatDetails.postFormatFunction;

  function encodePattern(pattern) {
    var numberFormatPattern = pattern.trim().match(/[#0,.]+/)[0];
    var split = numberFormatPattern.split('.');
    var c = split[0];
    var m = split[1];
    var groups = c.split(',');
    var groupLengths = groups.map(function (g) {
      return g.length;
    });
    var zeroLength = (groups[groups.length - 1].match(/0/g) || []).length;
    var decimalPlaces = typeof m === 'undefined' ? 0 : m.length;
    var paddingSplit = pattern.split(numberFormatPattern);
    var encodedPattern = {
      pattern: pattern,
      decimalPlaces: decimalPlaces,
      frontPadding: paddingSplit[0],
      backPadding: paddingSplit[1],
      groupLengths: groupLengths,
      zeroLength: zeroLength
    };
    return encodedPattern;
  }

  function pad(n, width) {
    n = n + '';
    return n.length >= width ? n : new Array(width - n.length + 1).join('0') + n;
  }

  function format(n, f) {
    var formattedPreNumber = toFixed(Math.abs(n), f.decimalPlaces);
    var splitNumber = formattedPreNumber.split('.');
    var segment = '';
    var cursor = splitNumber[0].length;
    var maxGroupIndex = f.groupLengths.length - 1;
    var groupIndex = maxGroupIndex;

    if (maxGroupIndex > 0) {
      while (cursor > 0) {
        if (groupIndex < 1) {
          groupIndex = 1;
        }

        var currentGroupLength = f.groupLengths[groupIndex];
        var start = cursor - currentGroupLength;
        segment = splitNumber[0].substring(start, cursor) + f.group + segment;
        cursor -= currentGroupLength;
        --groupIndex;
      }

      segment = segment.substring(0, segment.length - 1);
    } else {
      segment = splitNumber[0];
    }

    if (segment.length < f.zeroLength) {
      segment = pad(segment, f.zeroLength);
    }

    var formattedNumber = f.frontPadding + formatted + f.backPadding;
    return formattedNumber.replace(/!/g, symbol).trim();
  }

  var patternArray = pattern.split(';');
  var positiveFormat = encodePattern(patternArray[0]);
  positiveFormat.symbol = symbol;
  positiveFormat.decimal = decimal;
  positiveFormat.group = group;
  var negativeFormat = typeof patternArray[1] === 'undefined' ? encodePattern('-' + patternArray[0]) : encodePattern(patternArray[1]);
  negativeFormat.symbol = symbol;
  negativeFormat.decimal = decimal;
  negativeFormat.group = group;
  var zero = typeof patternArray[2] === 'undefined' ? format(0, positiveFormat) : patternArray[2];
  return function (n) {
    if (isNaN(n)) {
      return valueOnError;
    }

    var formattedNumber;
    n = Number(n);

    if (n > 0) {
      formattedNumber = format(n, positiveFormat);
    } else if (n === 0) {
      formattedNumber = zero.replace('!', symbol);
    } else {
      formattedNumber = format(n, negativeFormat);
    }

    return typeof postFormatFunction === 'function' ? postFormatFunction(n, formattedNumber) : formattedNumber;
  };
}

var FormatCurrency = forwardRef(function (_ref, ref) {
  var _cx;

  var customClassName = _ref.className,
      controlledColor = _ref.color,
      currency = _ref.currency,
      _ref$format = _ref.format,
      format = _ref$format === void 0 ? 'short' : _ref$format,
      _ref$hasColor = _ref.hasColor,
      hasColor = _ref$hasColor === void 0 ? false : _ref$hasColor,
      id = _ref.id,
      _ref$isCompact = _ref.isCompact,
      isCompact = _ref$isCompact === void 0 ? false : _ref$isCompact,
      _ref$isUnstyled = _ref.isUnstyled,
      isUnstyled = _ref$isUnstyled === void 0 ? false : _ref$isUnstyled,
      _ref$locale = _ref.locale,
      locale = _ref$locale === void 0 ? 'en-GB' : _ref$locale,
      _ref$maximumFractionD = _ref.maximumFractionDigits,
      maximumFractionDigits = _ref$maximumFractionD === void 0 ? 2 : _ref$maximumFractionD,
      _ref$minimumFractionD = _ref.minimumFractionDigits,
      minimumFractionDigits = _ref$minimumFractionD === void 0 ? 2 : _ref$minimumFractionD,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? 0 : _ref$value,
      rest = _objectWithoutProperties(_ref, _excluded);

  var formatConfig = {
    notation: isCompact ? 'compact' : 'standard',
    signDisplay: 'never',
    compactDisplay: 'short',
    maximumFractionDigits: maximumFractionDigits,
    minimumFractionDigits: minimumFractionDigits
  };
  var getCurrencyCode = getSymbolFromCurrency(currency);
  var formattedValue = new Intl.NumberFormat(locale, formatConfig).format(value);
  var currencyCode = getCurrencyCode === currency ? '' : currency;
  var currencyFormatted = getFormatter({
    currency: currency,
    locale: locale,
    formatted: formattedValue
  });
  var numberSign = Math.sign(value);

  var numberSignSwitch = function numberSignSwitch(sign) {
    return {
      1: 'positive',
      0: 'zero',
      '-1': 'negative'
    }[sign];
  };

  var controlledColorSwitch = function controlledColorSwitch(status) {
    return {
      positive: 'positive',
      neutral: 'neutral',
      negative: 'negative'
    }[status] || 'neutral';
  };

  var color = controlledColor && controlledColorSwitch(controlledColor) || hasColor && numberSignSwitch(numberSign);
  var className = cx((_cx = {}, _defineProperty(_cx, "".concat(prefix, "-format-currency"), true), _defineProperty(_cx, "".concat(prefix, "-format-currency__").concat(color), controlledColor || hasColor), _defineProperty(_cx, customClassName, !!customClassName), _cx));
  var explicitFormat = format === 'explicit' ? currencyCode : '';
  var output = String(currencyFormatted(value) + ' ' + explicitFormat).trim();
  return isUnstyled ? output : _jsx(StyledFormatCurrency, _objectSpread(_objectSpread({
    ref: ref,
    className: className
  }, rest), {}, {
    children: output
  }));
});
FormatCurrency.displayName = 'FormatCurrency';
export default FormatCurrency;